<template>
  <div class="step-form step-form--centered">
    <h2 class="step-form__title">{{ $t('step9.title') }}</h2>
    <p class="step-form__subtitle">{{ $t('step9.subtitle') }}</p>

    <!-- loading -->
    <div v-if="loading" class="slot-state">
      <span class="slot-spinner"></span>
      {{ $t('step9.loading') }}
    </div>

    <!-- load failed / nothing free: lead can continue, we call them instead -->
    <div v-else-if="loadFailed" class="slot-state slot-state--info">
      {{ $t('step9.error') }}
    </div>
    <div v-else-if="!days.length" class="slot-state slot-state--info">
      {{ $t('step9.noSlots') }}
    </div>

    <template v-else>
      <!-- day strip -->
      <div class="day-strip">
        <button
          v-for="d in days"
          :key="d.date"
          type="button"
          class="day-chip"
          :class="{ 'day-chip--selected': selectedDate === d.date }"
          @click="selectDay(d.date)"
        >
          <span class="day-chip__weekday">{{ weekdayOf(d.date) }}</span>
          <span class="day-chip__date">{{ dayLabelOf(d.date) }}</span>
        </button>
      </div>

      <!-- time grid for the selected day -->
      <div class="slot-grid">
        <button
          v-for="t in slotsOfSelectedDay"
          :key="t"
          type="button"
          class="slot-chip"
          :class="{ 'slot-chip--selected': isSelected(t) }"
          @click="selectSlot(t)"
        >
          {{ t }}
        </button>
      </div>

      <p class="slot-note">{{ $t('step9.note') }}</p>

      <button type="button" class="slot-skip" :class="{ 'slot-skip--active': skipped }" @click="skip">
        {{ $t('step9.skip') }}
      </button>
    </template>
  </div>
</template>

<script>
import { fetchSlots } from '../../services/submitForm'

export default {
  name: 'TimeSlot',
  props: {
    formData: { type: Object, required: true },
  },
  data() {
    return {
      loading: true,
      loadFailed: false,
      days: [],
      selectedDate: '',
    }
  },
  computed: {
    slotsOfSelectedDay() {
      const day = this.days.find(d => d.date === this.selectedDate)
      return day ? day.slots : []
    },
    skipped() {
      return this.formData.meetingSlot === 'SKIP'
    },
  },
  async mounted() {
    try {
      this.days = await fetchSlots()
      if (!this.days.length) {
        // nothing bookable — don't block the lead
        this.$emit('update', { meetingSlot: 'SKIP' })
      } else {
        // preselect the day of an already chosen slot (back-navigation), else the first day
        const chosen = this.formData.meetingSlot
        if (chosen && chosen !== 'SKIP') {
          this.selectedDate = chosen.split(' ')[0]
        }
        if (!this.days.some(d => d.date === this.selectedDate)) {
          this.selectedDate = this.days[0].date
        }
      }
    } catch (e) {
      this.loadFailed = true
      this.$emit('update', { meetingSlot: 'SKIP' })
    } finally {
      this.loading = false
    }
  },
  methods: {
    selectDay(date) {
      this.selectedDate = date
    },
    selectSlot(time) {
      this.$emit('update', { meetingSlot: this.selectedDate + ' ' + time })
    },
    isSelected(time) {
      return this.formData.meetingSlot === this.selectedDate + ' ' + time
    },
    skip() {
      this.$emit('update', { meetingSlot: 'SKIP' })
    },
    weekdayOf(date) {
      const locale = this.$i18n.locale === 'de' ? 'de-DE' : 'en-GB'
      return new Date(date + 'T00:00:00').toLocaleDateString(locale, { weekday: 'short' })
    },
    dayLabelOf(date) {
      const locale = this.$i18n.locale === 'de' ? 'de-DE' : 'en-GB'
      return new Date(date + 'T00:00:00').toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
    },
  },
}
</script>

<style lang="scss" scoped>
.step-form {
  width: 100%;

  &--centered {
    text-align: center;
  }

  &__title {
    font-size: clamp(1.4rem, 3.5vw, 2rem);
    font-weight: 700;
    background: linear-gradient(120deg, var(--white), var(--gold));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 8px;
  }

  &__subtitle {
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.95rem;
    margin-bottom: 24px;
  }
}

.slot-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;

  &--info {
    background: rgba(208, 176, 146, 0.08);
    border: 1.5px solid rgba(208, 176, 146, 0.3);
    border-radius: 12px;
  }
}

.slot-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: slot-spin 0.8s linear infinite;
}

@keyframes slot-spin {
  to { transform: rotate(360deg); }
}

.day-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 2px 10px;
  margin-bottom: 14px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(208, 176, 146, 0.35);
    border-radius: 2px;
  }
}

.day-chip {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1.5px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: var(--white);
  cursor: pointer;
  transition: all 0.15s ease;

  &__weekday {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.45);
  }

  &__date {
    font-size: 0.95rem;
    font-weight: 600;
  }

  &:hover {
    border-color: rgba(208, 176, 146, 0.5);
  }

  &--selected {
    border-color: var(--gold);
    background: rgba(208, 176, 146, 0.12);

    .day-chip__weekday {
      color: var(--gold);
    }
  }
}

.slot-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding: 2px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(208, 176, 146, 0.35);
    border-radius: 2px;
  }
}

.slot-chip {
  padding: 11px 0;
  border-radius: 10px;
  border: 1.5px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: var(--white);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(208, 176, 146, 0.5);
  }

  &--selected {
    border-color: var(--gold);
    background: var(--gold);
    color: var(--navy);
    box-shadow: 0 0 0 3px rgba(208, 176, 146, 0.15);
  }
}

.slot-note {
  margin-top: 14px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.35);
}

.slot-skip {
  margin-top: 10px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: var(--gold);
  }

  &--active {
    color: var(--gold);
    text-decoration: none;

    &::before {
      content: '✓ ';
    }
  }
}
</style>
